// Enhanced video thumbnail generator with improved black frame detection
// Works for both standard and WhatsApp videos

// Logger utility for debugging
const Logger = {
  info: (message: string, details?: any) => {
    if (process.env.NODE_ENV === "development") {
      console.log(`[INFO] ${message}`, details || "");
    }
  },
  warn: (message: string, details?: any) => {
    if (process.env.NODE_ENV === "development") {
      console.warn(`[WARN] ${message}`, details || "");
    }
  },
  error: (message: string, details?: any) => {
    if (process.env.NODE_ENV === "development") {
      console.error(`[ERROR] ${message}`, details || "");
    }
  },
  success: (message: string, details?: any) => {
    if (process.env.NODE_ENV === "development") {
      console.log(`[SUCCESS] ${message}`, details || "");
    }
  },
  debug: (message: string, details?: any) => {
    if (process.env.NODE_ENV === "development") {
      console.log(`[DEBUG] ${message}`, details || "");
    }
  },
};

// Options interface for thumbnail generation
export interface GenerateThumbnailOptions {
  seekToSeconds?: number // At what second to capture the thumbnail
  imageType?: "image/jpeg" | "image/webp"
  quality?: number // 0 to 1
  maxWidth?: number // Resize to maximum width
  timeout?: number // Timeout in milliseconds
}

// Detect iOS device
const isIOS = (): boolean => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  )
}

// Detect WhatsApp video
const isWhatsAppVideo = (file: File): boolean => {
  return (
    file.name.includes("WA") ||
    file.name.toLowerCase().includes("whatsapp") ||
    // Additional check for WhatsApp video naming patterns
    /VID-\d{8}-WA\d{4}/.test(file.name)
  )
}

// Generate video thumbnail with improved robustness
export const generateVideoThumbnail = (file: File, options: GenerateThumbnailOptions = {}): Promise<Blob | null> => {
  // Detect special video types
  const isIOSDevice = isIOS()
  const isMOVFile = file.name.toLowerCase().endsWith(".mov") || file.type === "video/quicktime"
  const isWAVideo = isWhatsAppVideo(file)

  // Set default options based on video type
  const {
    seekToSeconds = isWAVideo ? 2.0 : 0.1,
    imageType = "image/jpeg",
    quality = 0.8,
    maxWidth,
    timeout = isWAVideo ? 20000 : 15000,
  } = options

  return new Promise((resolve) => {
    Logger.info(`Starting thumbnail generation for ${file.name}`, {
      type: file.type,
      size: `${(file.size / 1024).toFixed(1)} KB`,
      seekTo: seekToSeconds,
      isWhatsApp: isWAVideo,
    })

    if (isIOSDevice && isMOVFile) {
      Logger.info(`iOS device detected with MOV file, using specialized handling`)
    }

    if (isWAVideo) {
      Logger.info(`WhatsApp video detected, using specialized handling`)
    }

    const video = document.createElement("video")
    const url = URL.createObjectURL(file)

    // Set up video element with best practices for cross-browser support
    video.src = url
    video.crossOrigin = "anonymous"
    video.muted = true
    video.playsInline = true
    video.preload = "metadata"
    video.controls = false

    // iOS-specific attributes
    video.setAttribute("playsinline", "")
    video.setAttribute("webkit-playsinline", "")

    // For iOS MOV files and WhatsApp videos, we need to ensure autoplay is false initially
    video.autoplay = false

    // Track if we've already resolved to prevent multiple resolutions
    let hasResolved = false
    let timeoutId: number | null = null
    let processingStage = "initial"

    // Define seek positions based on video type
    // All videos now get multiple seek attempts, but with different positions
    const seekPositions = isWAVideo
      ? [seekToSeconds, 0.1, 0.3, 0.5, 1.0, 2.0] // WhatsApp videos
      : [seekToSeconds, 0.1, 0.3, 0.5, 1.0, 2.0];     // Standard videos

    let currentSeekIndex = 0

    // Clean up function
    const cleanUp = () => {
      if (timeoutId) clearTimeout(timeoutId)
      URL.revokeObjectURL(url)
      try {
        document.body.removeChild(video)
      } catch (e) {
        // Ignore if already removed or not added
      }
    }

    // Safe resolve function
    const safeResolve = (result: Blob | null) => {
      if (!hasResolved) {
        hasResolved = true
        cleanUp()
        resolve(result)
      }
    }

    // Set a timeout for the entire process
    timeoutId = window.setTimeout(() => {
      if (!hasResolved) {
        Logger.warn(`Thumbnail generation timed out at stage: ${processingStage} for ${file.name}`)
        safeResolve(null)
      }
    }, timeout)

    // Function to capture the frame
    const captureFrame = () => {
      processingStage = "capturing_frame"
      try {
        // Check if video has dimensions
        if (!video.videoWidth || !video.videoHeight) {
          Logger.warn(`Video dimensions not available`)

          // Try next seek position
          if (currentSeekIndex < seekPositions.length - 1) {
            currentSeekIndex++
            trySeek(seekPositions[currentSeekIndex])
            return
          }

          safeResolve(null)
          return
        }

        // Create canvas with appropriate dimensions
        const canvas = document.createElement("canvas")
        let finalWidth = video.videoWidth
        let finalHeight = video.videoHeight

        // Apply max width if specified
        if (maxWidth && finalWidth > maxWidth) {
          const ratio = maxWidth / finalWidth
          finalWidth = maxWidth
          finalHeight = finalHeight * ratio
        }

        canvas.width = finalWidth
        canvas.height = finalHeight

        // Add willReadFrequently attribute to address the warning
        const ctx = canvas.getContext("2d", { willReadFrequently: true })
        if (!ctx) {
          Logger.error("Failed to get canvas context")
          safeResolve(null)
          return
        }

        // Draw the current frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

        // Check if the frame is not just black
        // Use appropriate thresholds based on video type
        const frameCheckResult = checkFrameContent(ctx, canvas.width, canvas.height, isWAVideo);

        if (!frameCheckResult.isValid) {
          Logger.warn(`Frame appears to be black or nearly black at ${video.currentTime}s: ${frameCheckResult.reason}`)

          // Try next seek position
          if (currentSeekIndex < seekPositions.length - 1) {
            currentSeekIndex++
            trySeek(seekPositions[currentSeekIndex])
            return
          }

          safeResolve(null)
          return
        }

        Logger.debug(`Frame at ${video.currentTime}s passed content check: ${frameCheckResult.details}`)

        // Convert to blob with specified format and quality
        canvas.toBlob(
          (blob) => {
            if (blob && blob.size > 100) {
              Logger.success(`Successfully captured frame at ${video.currentTime}s, size: ${blob.size} bytes`)
              safeResolve(blob)
            } else {
              Logger.error(`Failed to create blob or blob too small`)
              safeResolve(null)
            }
          },
          imageType,
          quality,
        )
      } catch (e) {
        Logger.error(`Error capturing frame: ${e}`)
        safeResolve(null)
      }
    }

    // Enhanced frame content check with detailed reporting
    const checkFrameContent = (
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number,
      isWhatsAppVideo: boolean
    ): { isValid: boolean; reason: string; details: string } => {
      // Sample pixels from different areas of the canvas
      const checkPoints = [
        { x: Math.floor(width / 2), y: Math.floor(height / 2) },         // center
        { x: Math.floor(width / 4), y: Math.floor(height / 4) },         // top-left quadrant
        { x: Math.floor((width * 3) / 4), y: Math.floor(height / 4) },   // top-right quadrant
        { x: Math.floor(width / 4), y: Math.floor((height * 3) / 4) },   // bottom-left quadrant
        { x: Math.floor((width * 3) / 4), y: Math.floor((height * 3) / 4) }, // bottom-right quadrant
      ]

      // Use appropriate threshold based on video type
      const threshold = isWhatsAppVideo ? 12 : 15;

      // Store pixel values for debugging
      const pixelValues: { position: string; r: number; g: number; b: number; luminance: number }[] = [];

      // Check individual points
      for (let i = 0; i < checkPoints.length; i++) {
        const point = checkPoints[i];
        const pixel = ctx.getImageData(point.x, point.y, 1, 1).data;
        const luminance = 0.299 * pixel[0] + 0.587 * pixel[1] + 0.114 * pixel[2];

        pixelValues.push({
          position: `point${i + 1}`,
          r: pixel[0],
          g: pixel[1],
          b: pixel[2],
          luminance
        });

        // Check if pixel is not black (using appropriate threshold)
        if (pixel[0] > threshold || pixel[1] > threshold || pixel[2] > threshold) {
          return {
            isValid: true,
            reason: "Point check passed",
            details: `Point ${i + 1} passed with RGB(${pixel[0]},${pixel[1]},${pixel[2]}), luminance: ${luminance.toFixed(2)}`
          };
        }
      }

      // If individual points didn't work, check average luminance of a central region
      try {
        // Sample a central region (20% of width/height)
        const regionWidth = Math.max(20, Math.floor(width * 0.2));
        const regionHeight = Math.max(20, Math.floor(height * 0.2));
        const regionX = Math.floor((width - regionWidth) / 2);
        const regionY = Math.floor((height - regionHeight) / 2);

        const imageData = ctx.getImageData(regionX, regionY, regionWidth, regionHeight);
        const data = imageData.data;

        // Calculate average luminance
        let totalLuminance = 0;
        let maxLuminance = 0;

        for (let i = 0; i < data.length; i += 4) {
          // Simple luminance formula: 0.299*R + 0.587*G + 0.114*B
          const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          totalLuminance += luminance;
          maxLuminance = Math.max(maxLuminance, luminance);
        }

        const avgLuminance = totalLuminance / (regionWidth * regionHeight);

        // Use appropriate threshold based on video type
        const luminanceThreshold = isWhatsAppVideo ? 10 : 12;
        const maxLuminanceThreshold = isWhatsAppVideo ? 25 : 30;

        // Check both average and max luminance
        if (avgLuminance > luminanceThreshold || maxLuminance > maxLuminanceThreshold) {
          return {
            isValid: true,
            reason: "Region check passed",
            details: `Region check passed with avg luminance: ${avgLuminance.toFixed(2)}, max luminance: ${maxLuminance.toFixed(2)}`
          };
        }

        return {
          isValid: false,
          reason: "Both point and region checks failed",
          details: `Points: ${JSON.stringify(pixelValues)}, Region avg: ${avgLuminance.toFixed(2)}, max: ${maxLuminance.toFixed(2)}`
        };
      } catch (e) {
        Logger.warn(`Error checking region luminance: ${e}`);
        return {
          isValid: false,
          reason: "Point check failed and region check error",
          details: `Points: ${JSON.stringify(pixelValues)}, Error: ${e}`
        };
      }
    }

    // Try to seek to a specific time
    const trySeek = (seekTime: number) => {
      processingStage = `seeking_to_${seekTime}`
      Logger.info(`Trying to seek to ${seekTime}s`)

      try {
        // For iOS and WhatsApp videos, we need a special approach
        if ((isIOSDevice && isMOVFile) || isWAVideo) {
          // First play the video to ensure it's loaded
          video.play()
            .then(() => {
              // Then pause it immediately
              video.pause()
              // Now try to set the current time
              try {
                video.currentTime = seekTime
              } catch (e) {
                Logger.warn(`Error setting currentTime: ${e}`)
                // Try next seek position
                if (currentSeekIndex < seekPositions.length - 1) {
                  currentSeekIndex++
                  trySeek(seekPositions[currentSeekIndex])
                } else {
                  safeResolve(null)
                }
              }
            })
            .catch((e) => {
              Logger.warn(`Play failed: ${e.message}`)
              // Try to set current time anyway
              try {
                video.currentTime = seekTime
              } catch (e) {
                Logger.warn(`Error setting currentTime after play failed: ${e}`)
                // Try next seek position
                if (currentSeekIndex < seekPositions.length - 1) {
                  currentSeekIndex++
                  trySeek(seekPositions[currentSeekIndex])
                } else {
                  safeResolve(null)
                }
              }
            })
        } else {
          // Standard approach for other platforms
          video.currentTime = seekTime
        }
      } catch (e) {
        Logger.warn(`Error in trySeek: ${e}`)
        // Try next seek position
        if (currentSeekIndex < seekPositions.length - 1) {
          currentSeekIndex++
          trySeek(seekPositions[currentSeekIndex])
        } else {
          safeResolve(null)
        }
      }
    }

    // Event handlers
    video.addEventListener("loadedmetadata", () => {
      processingStage = "metadata_loaded"
      Logger.info(`Video metadata loaded, duration: ${video.duration}s`)

      // Adjust seek positions if video is shorter than requested
      if (video.duration < seekPositions[0]) {
        seekPositions[0] = 0
      }

      // Start with the first seek attempt
      trySeek(seekPositions[currentSeekIndex])
    })

    video.addEventListener("seeked", () => {
      processingStage = `seeked_to_${video.currentTime}`
      Logger.info(`Video seeked to ${video.currentTime}s`)

      // Add a small delay before capturing the frame
      // Use a shorter delay for standard videos
      setTimeout(() => {
        captureFrame()
      }, isWAVideo ? 50 : 20)
    })

    video.addEventListener("error", (e) => {
      const errorCode = video.error ? video.error.code : "unknown"
      const errorMessage = video.error ? video.error.message : "unknown error"
      Logger.error(`Video error: code=${errorCode}, message=${errorMessage}`)
      safeResolve(null)
    })

    // Append to DOM temporarily
    document.body.appendChild(video)
    video.style.position = "absolute"
    video.style.width = "1px"
    video.style.height = "1px"
    video.style.opacity = "0.01"
    video.style.pointerEvents = "none"
    video.style.zIndex = "-1000"
  })
}

// Create a fallback thumbnail when video thumbnail generation fails
export const createFallbackThumbnail = (filename: string, width = 720, height = 1280): Promise<Blob> => {
  return new Promise((resolve) => {
    Logger.info(`Creating fallback thumbnail for ${filename}`)

    // Create canvas
    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      Logger.error(`Failed to get canvas context for fallback thumbnail`)
      // If canvas context fails, create a 1x1 transparent pixel as absolute fallback
      const emptyBlob = new Blob([new Uint8Array([0, 0, 0, 0])], { type: "image/png" })
      resolve(emptyBlob)
      return
    }

    // Create a gradient background
    const gradient = ctx.createLinearGradient(0, 0, width, height)
    gradient.addColorStop(0, "#3498db")
    gradient.addColorStop(1, "#2c3e50")
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)

    // Add a play button icon
    ctx.fillStyle = "rgba(255, 255, 255, 0.8)"
    const centerX = width / 2
    const centerY = height / 2
    const radius = Math.min(width, height) * 0.15

    // Draw circle
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
    ctx.fill()

    // Draw triangle (play icon)
    ctx.fillStyle = "#3498db"
    const triangleSize = radius * 0.8
    ctx.beginPath()
    ctx.moveTo(centerX + triangleSize / 2, centerY)
    ctx.lineTo(centerX - triangleSize / 2, centerY - triangleSize / 2)
    ctx.lineTo(centerX - triangleSize / 2, centerY + triangleSize / 2)
    ctx.closePath()
    ctx.fill()

    // Add filename text
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)"
    ctx.font = `bold ${Math.floor(width * 0.05)}px Arial, sans-serif`
    ctx.textAlign = "center"

    // Truncate filename if too long
    let displayName = filename
    if (displayName.length > 20) {
      displayName = displayName.substring(0, 17) + "..."
    }

    // Position text at bottom
    ctx.fillText(displayName, centerX, height - height * 0.1)

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          Logger.success(`Fallback thumbnail created successfully, size: ${blob.size} bytes`)
          resolve(blob)
        } else {
          Logger.error(`Blob creation failed for fallback`)
          // Create a 1x1 transparent pixel as absolute fallback
          const emptyBlob = new Blob([new Uint8Array([0, 0, 0, 0])], { type: "image/png" })
          resolve(emptyBlob)
        }
      },
      "image/jpeg",
      0.9,
    )
  })
}

// This function manages the thumbnail generation process with fallbacks
export const getThumbnail = async (
  file: File,
  options: GenerateThumbnailOptions = {},
): Promise<{
  blob: Blob
  isGenerated: boolean
}> => {
  try {
    Logger.info(`Starting thumbnail generation process for ${file.name}`, {
      size: `${(file.size / 1024).toFixed(1)} KB`,
      type: file.type,
    })

    // Start preparing fallback in parallel
    const fallbackPromise = createFallbackThumbnail(file.name)

    // Try to generate a real thumbnail
    const thumbnailBlob = await generateVideoThumbnail(file, options)

    if (thumbnailBlob) {
      Logger.success(`Thumbnail generated successfully for ${file.name}`)
      return {
        blob: thumbnailBlob,
        isGenerated: true,
      }
    }

    // If thumbnail generation failed, use the fallback
    Logger.warn(`Real thumbnail generation failed, using fallback for ${file.name}`)
    const fallbackBlob = await fallbackPromise

    return {
      blob: fallbackBlob,
      isGenerated: false,
    }
  } catch (error) {
    Logger.error("Error in thumbnail manager:", error)

    // Create fallback on error
    try {
      const fallbackBlob = await createFallbackThumbnail(file.name)
      return {
        blob: fallbackBlob,
        isGenerated: false,
      }
    } catch (fallbackError) {
      Logger.error("Error creating fallback thumbnail:", fallbackError)

      // Create an absolute minimal fallback - a 1x1 transparent pixel
      const minimalFallback = new Blob([new Uint8Array([0, 0, 0, 0])], { type: "image/png" })
      return {
        blob: minimalFallback,
        isGenerated: false,
      }
    }
  }
}